import type { FormInstance } from "../../../../../../../shared/frontend/ui";
import { useFormDirty } from "../../../../../../../shared/frontend/forms/useFormDirty";
import { useEffect, useEffectEvent } from "react";
import { Form, Input, InputNumber, Select, Space, Typography } from "../../../../../../../shared/frontend/ui";
import { DatePicker, RangePicker } from "../../../../../../../shared/frontend/components/ControlledPickers";
import type { DefaultOptionType } from "../../../../../../../shared/frontend/ui";
import dayjs,{ Dayjs } from "dayjs";
import type { I18nContextValue } from "../../../app/i18n/useI18n";
import { useLastPaymentMethod } from "../../../app/prefs/useLastPaymentMethod";
import type { UnitsSettings } from "../../../app/units/units";
import { PAYMENT_METHOD_CASH } from "../../../app/utils/paymentMethodCodes";
import { FormDrawer } from "../../../components/FormDrawer";
import type { ContributionCalendarRow } from "./personDetailTypes";
import { formatDateOnlyIso } from "./personDetailUtils";
import type { Feedback } from "../../../../../../../shared/frontend/feedback/useFeedback";
import { MIN_PAYABLE_AMOUNT } from "./personAmountThresholds";
import { useFinanceOperation } from "../../../app/operations/useFinanceOperation";

export type ContributionBulkPayFormValues = {
  months?: [Dayjs, Dayjs] | null;
  amount?: number;
  paidDate?: Dayjs;
  method?: string;
  reference?: string;
};

type ApiClient = {
  post: (url: string, body?: unknown) => Promise<unknown>;
};

type Props = {
  personId: string;
  open: boolean;
  form: FormInstance<ContributionBulkPayFormValues>;
  contributions: ContributionCalendarRow[];
  api: ApiClient;
  message: Feedback;
  text: I18nContextValue["text"];
  decimalSeparator: string;
  currencyCode: string;
  moneyPlaceholder: string;
  units: UnitsSettings;
  dateInputFormat: string | string[];
  paymentMethodSelectOptions: DefaultOptionType[];
  onClose: () => void;
  onContributionsChange: (contributions: ContributionCalendarRow[]) => void;
  onReloadContributions: () => void;
};

export function PersonContributionBulkPayDrawer({
  personId,
  open,
  form,
  contributions,
  api,
  message,
  text,
  decimalSeparator,
  currencyCode,
  moneyPlaceholder,
  dateInputFormat,
  paymentMethodSelectOptions,
  onClose,
  onContributionsChange,
  onReloadContributions
}: Props) {
  const operation = useFinanceOperation(personId);
  const { dirty, initialize } = useFormDirty(form);
  const { rememberPaymentMethod, resolveDefaultPaymentMethod } = useLastPaymentMethod();
  const initializePayment = useEffectEvent(() => {
    const fallbackMethod = paymentMethodSelectOptions.find((opt) => Number(opt.value) === PAYMENT_METHOD_CASH)?.value ?? paymentMethodSelectOptions[0]?.value ?? null;
    const defaultMethod = resolveDefaultPaymentMethod(paymentMethodSelectOptions, null) ?? fallbackMethod;
    initialize({ paidDate: dayjs(), method: defaultMethod != null ? String(defaultMethod) : undefined, reference: undefined });
  });
  useEffect(() => { if (open) initializePayment(); }, [open]);

  const handleClose = () => {
    onClose();
    form.resetFields();
    onContributionsChange([]);
  };

  return (
    <FormDrawer mode="edit"
      title="Payment for multiple contributions"
      dirty={dirty}
      open={open}
      onCancel={handleClose}
      formId="contribution-bulk-pay-form"
      submitting={operation.pending}
      submitText="Record payment"
    >
      <Form
        id="contribution-bulk-pay-form"
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={async (values) => {
          if (!values.months || !values.months[0] || !values.months[1]) {
            message.warning(text("Please select a period."));
            return;
          }
          if (!values.amount || values.amount <= 0) {
            message.warning(text("Please enter an amount."));
            return;
          }
          if (contributions.length === 0) {
            message.warning(text("No contributions found for selected months."));
            return;
          }

          try {
            const result = await operation.execute(async (committed) => {
              const paidDateIso = values.paidDate ? formatDateOnlyIso(values.paidDate) : undefined;
              const totalAmount = Number(values.amount || 0);
              let remainingAmount = totalAmount;
              const sortedContributions = [...contributions].sort((a, b) => String(a.dueDate || "").localeCompare(String(b.dueDate || "")));
              const paymentRequests: Promise<unknown>[] = [];
              for (const contrib of sortedContributions) {
                if (remainingAmount <= MIN_PAYABLE_AMOUNT) break;
                const amount = Math.min(remainingAmount, Number(contrib.openAmount || 0));
                if (amount <= MIN_PAYABLE_AMOUNT) continue;
                paymentRequests.push(api.post(`/api/v1/open-items/${encodeURIComponent(contrib.id)}/pay`, {
                  amount,
                  paidDate: paidDateIso || undefined,
                  method: values.method || null,
                  reference: values.reference || null
                }).then(result => { committed(); return result; }));
                remainingAmount -= amount;
              }

              const results = await Promise.allSettled(paymentRequests);
              const failure = results.find(result => result.status === "rejected");
              if (failure?.status === "rejected") throw failure.reason;
              rememberPaymentMethod(values.method);
              message.success(text("Payments recorded."));
              handleClose();
            });
            if (result === "refreshFailed" || result === "writeUncertain") handleClose();
          } catch {
            // Central API toast shows error.
          }
        }}
      >
        <Form.Item
          name="months"
          label={text("Period")}
          rules={[{ required: true, message: text("Please select a period.") }]}
          tooltip={text("Select start and end month. All contributions in this period will be considered.")}
        >
          <RangePicker
            picker="month"
            style={{ width: "100%" }}
            format="YYYY-MM"
            onChange={onReloadContributions}
          />
        </Form.Item>

        {contributions.length > 0 && (
          <Form.Item label={text("Selected contributions")}>
            <Typography.Text type="secondary">
              {text("contributions selected").replace("{0}", String(contributions.length))}
            </Typography.Text>
          </Form.Item>
        )}

        <Form.Item
          name="amount"
          label={text("Payment amount")}
          rules={[{ required: true, message: text("Amount is required") }]}
        >
          <Space.Compact style={{ width: "100%", display: "flex" }}>
            <InputNumber
              style={{ flex: 1, minWidth: 0 }}
              min={0.01}
              precision={2}
              step={0.01}
              decimalSeparator={decimalSeparator}
              placeholder={moneyPlaceholder}
            />
            <Input
              aria-hidden="true"
              readOnly
              tabIndex={-1}
              value={currencyCode}
              style={{ width: 64, textAlign: "center", pointerEvents: "none" }}
            />
          </Space.Compact>
        </Form.Item>

        <Form.Item
          name="paidDate"
          label={text("Payment date")}
          rules={[
            { required: true, message: text("Payment date is required") },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                const selectedDate = dayjs(value);
                const today = dayjs().startOf("day");
                if (selectedDate.isAfter(today, "day")) {
                  return Promise.reject(new Error(text("Payment date must not be in the future")));
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <DatePicker
            style={{ width: "100%" }}
            format={dateInputFormat}
            allowClear={false}
            disabledDate={(current) => current ? current.isAfter(dayjs().endOf("day"), "day") : false}
          />
        </Form.Item>

        <Form.Item
          name="method"
          label={text("Payment method")}
          rules={[{ required: true, message: text("Payment method is required") }]}
        >
          <Select
            options={paymentMethodSelectOptions}
            placeholder={text("Select payment method…")}
          />
        </Form.Item>

        <Form.Item
          name="reference"
          label={text("Reference")}
        >
          <Input.TextArea
            rows={2}
            placeholder={text("Optional: Reference or note")}
          />
        </Form.Item>
      </Form>
    </FormDrawer>
  );
}
